import { Player, SimilarPlayer } from '../types/Player';

// Global cache for loaded data
interface PlayerDataRow {
  player: Player;
  neighborIndices: number[];
  neighborScores: number[];
  index: number;
}

export type Gender = 'men' | 'women';

let menPlayersDataCache: PlayerDataRow[] | null = null;
let womenPlayersDataCache: PlayerDataRow[] | null = null;

// Helper function to safely parse CSV line handling quoted fields
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

// Load all data from the single CSV file
async function loadAllData(gender: Gender = 'men'): Promise<PlayerDataRow[]> {
  const cache = gender === 'men' ? menPlayersDataCache : womenPlayersDataCache;
  
  if (cache) {
    console.log(`Using cached ${gender} data, count:`, cache.length);
    return cache;
  }

  try {
    const filename = gender === 'men' ? 'men_final_data_N50.csv' : 'women_final_data_N50.csv';
    console.log(`Loading data from ${filename}...`);
    const response = await fetch(`/${filename}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const csvText = await response.text();
    const lines = csvText.split('\n');
    const headers = parseCSVLine(lines[0]);
    console.log('CSV headers count:', headers.length);
    
    const data: PlayerDataRow[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const values = parseCSVLine(line);
      if (values.length < headers.length - 10) continue; // Allow some missing trailing columns
      
      // Helper to get value by header name
      const getValue = (headerName: string): string => {
        const idx = headers.indexOf(headerName);
        return idx !== -1 && values[idx] ? values[idx] : '';
      };
      
      const getFloat = (headerName: string): number => {
        const val = getValue(headerName);
        return val ? parseFloat(val) : 0;
      };
      
      const getInt = (headerName: string): number => {
        const val = getValue(headerName);
        return val ? parseInt(val) : 0;
      };
      
      // Extract player info
      const playerID = getValue('PlayerID');
      const playerName = getValue('Player');
      const playerFirstName = getValue('Player_First_Name');
      const playerLastName = getValue('Player_Last_Name');
      const team = getValue('Team');
      const position = getValue('Pos');
      const playerClass = getValue('Class');
      
      // Create player object
      const player: Player = {
        id: playerID || `player_${i}`,
        name: playerName || 'Unknown Player',
        firstName: playerFirstName || '',
        lastName: playerLastName || '',
        position: position || 'G',
        school: team || 'Unknown School',
        class: playerClass || 'SR',
        height: '6\'0"', // Not in CSV
        weight: 180, // Not in CSV
        stats: {
          pointsPerGame: getFloat('PTS_pgame'),
          reboundsPerGame: getFloat('TRB_pgame'),
          assistsPerGame: getFloat('AST_pgame'),
          stealsPerGame: getFloat('STL_pgame'),
          blocksPerGame: getFloat('BLK_pgame'),
          fieldGoalPercentage: getFloat('FG%'),
          threePointPercentage: getFloat('3P%'),
          freeThrowPercentage: getFloat('FT%'),
          trueShootingPercentage: getFloat('TS%'),
          effectiveFieldGoalPercentage: getFloat('eFG%'),
          efficiencyRating: getFloat('PER'),
          winShares: getFloat('WS'),
          offensiveRating: getFloat('ORtg'),
          defensiveRating: getFloat('DRtg'),
          usageRate: getFloat('USG%'),
          minutesPlayed: getFloat('MP_pgame'),
          gamesPlayed: Math.round(getFloat('MP_totals') / (getFloat('MP_pgame') || 1)) // Calculate from totals
        },
        recruiting: {
          stars: 3, // Default
          offers: [],
          committed: false,
          commitmentSchool: ''
        }
      };
      
      // Extract neighbor data
      const neighborIndices: number[] = [];
      const neighborScores: number[] = [];
      
      for (let j = 1; j <= 49; j++) {
        const idx = getInt(`neighborindex_${j}`);
        const score = getFloat(`neighborsim_score_v2_${j}`);
        if (idx >= 0) {
          neighborIndices.push(idx);
          neighborScores.push(score);
        }
      }
      
      data.push({
        player,
        neighborIndices,
        neighborScores,
        index: getInt('index')
      });
    }
    
    if (gender === 'men') {
      menPlayersDataCache = data;
    } else {
      womenPlayersDataCache = data;
    }
    console.log(`Successfully loaded ${gender} data, count:`, data.length);
    return data;
  } catch (error) {
    console.error('Error loading data:', error);
    return [];
  }
}

// Note: Using real CSV data from men_final_data_N50.csv and women_final_data_N50.csv

// Search for players by name
export async function searchPlayers(query: string, gender: Gender = 'men'): Promise<Player[]> {
  console.log('Searching for:', query, 'in', gender);
  
  if (!query || query.trim().length === 0) {
    return [];
  }
  
  const allData = await loadAllData(gender);
  const searchTerm = query.toLowerCase().trim();
  
  const results = allData
    .filter(data => {
      const player = data.player;
      // Only search by name, first name, and last name
      return player.name.toLowerCase().includes(searchTerm) ||
             player.firstName.toLowerCase().includes(searchTerm) ||
             player.lastName.toLowerCase().includes(searchTerm);
    })
    .map(data => data.player)
    .slice(0, 50); // Limit results
  
  console.log('Found results:', results.length);
  return results;
}

// Find similar players using pre-computed similarity scores
export async function findSimilarPlayers(targetPlayer: Player, gender: Gender = 'men'): Promise<SimilarPlayer[]> {
  const allData = await loadAllData(gender);
  
  // Find the data row for the target player
  const targetData = allData.find(data => data.player.id === targetPlayer.id);
  
  if (!targetData) {
    console.error('No data found for player:', targetPlayer.id);
    return [];
  }
  
  const similarPlayers: SimilarPlayer[] = [];
  
  // Get the top 10 similar players using pre-computed scores
  for (let i = 0; i < Math.min(10, targetData.neighborIndices.length); i++) {
    const neighborIndex = targetData.neighborIndices[i];
    const similarityScore = targetData.neighborScores[i] || 0;
    
    // Find the player data at this index
    const similarData = allData.find(data => data.index === neighborIndex);
    
    if (similarData && similarData.player.id !== targetPlayer.id) {
      similarPlayers.push({
        player: similarData.player,
        similarityScore: Math.max(0, similarityScore), // Ensure non-negative
        reasons: [`Similarity score: ${(similarityScore * 100).toFixed(1)}%`]
      });
    }
  }
  
  console.log('Found similar players:', similarPlayers.length);
  return similarPlayers;
}

// Get a player by ID
export async function getPlayerById(id: string, gender: Gender = 'men'): Promise<Player | null> {
  const allData = await loadAllData(gender);
  const data = allData.find(d => d.player.id === id);
  return data ? data.player : null;
}
